import type {
  CreateTemplateCreationRecordResponse,
  GetTemplateCreationRecordResponse,
} from '../contracts';

export const stampTemplateCreationRecordUseSections = (
  record: CreateTemplateCreationRecordResponse,
  useSections: boolean | null | undefined
): CreateTemplateCreationRecordResponse =>
  typeof useSections === 'boolean' ? { ...record, use_sections: useSections } : record;

export const seedGetTemplateCreationRecordCache = (
  record: CreateTemplateCreationRecordResponse,
  useSections: boolean | null | undefined
): GetTemplateCreationRecordResponse => ({
  template_creation_record: stampTemplateCreationRecordUseSections(record, useSections),
  version_controls: [],
});

/**
 * GET often omits `use_sections`. Keep a cached POST boolean while this query
 * entry is still in React Query. After gcTime (unmount) or persisted-cache
 * expiry there is no previous boolean, so the form falls back to false until
 * GET echoes the field — then delete this merge.
 */
export const mergeTemplateCreationRecordUseSections = (
  incoming: GetTemplateCreationRecordResponse,
  previous: GetTemplateCreationRecordResponse | undefined
): GetTemplateCreationRecordResponse => {
  if (typeof incoming.template_creation_record.use_sections === 'boolean') {
    return incoming;
  }

  const previousUseSections = previous?.template_creation_record.use_sections;
  if (typeof previousUseSections !== 'boolean') {
    return incoming;
  }

  return {
    ...incoming,
    template_creation_record: {
      ...incoming.template_creation_record,
      use_sections: previousUseSections,
    },
  };
};

export const selectTemplateCreationRecordForForm = (
  data: GetTemplateCreationRecordResponse,
  selectedVersionControlId: string | undefined
) => {
  const selectedVersionControl = data.version_controls.find(
    (versionControl) => versionControl.id === selectedVersionControlId
  );

  return {
    transcript: data.template_creation_record.transcript,
    example: selectedVersionControl?.example_note ?? '',
    structure: selectedVersionControl?.template_structure ?? '',
    selectedVersionControlId: data.template_creation_record.selected_version_control_id,
    templateName: data.template_creation_record.template_name,
    templateId: data.template_creation_record.template_id,
    versionControls: data.version_controls,
    use_sections: data.template_creation_record.use_sections,
    // The raw row, not a derived boolean: resolving sectionedness lexes the
    // markdown, and this select re-runs on every shared-cache update —
    // including per-chunk streaming writes. Callers derive lazily
    // (idle-gated) via resolveVersionControlUseSections.
    selectedVersionControl,
  };
};

/**
 * Form hydration order: the selected version's resolved sectionedness wins —
 * the toggle reflects the version on screen — then the saved template's flag,
 * then the creation record's create-time intent. The version arg trails and
 * is optional so callers that have no version context keep the flag-only
 * resolution.
 */
export const resolveTemplateFormUseSections = (
  templateUseSections: boolean | null | undefined,
  creationRecordUseSections: boolean | null | undefined,
  selectedVersionUseSections?: boolean
): boolean =>
  selectedVersionUseSections ?? templateUseSections ?? creationRecordUseSections ?? false;
